/**
 * StatCard: KPI card del dashboard.
 *
 * Sprint 11 v2 S11-V2-02. Replica `.cb-stat` + `.cb-stat--warning` del
 * handoff (`docs/COMPONENTES.md § Stat`).
 *
 * Estructura: label arriba + valor grande al medio + meta abajo. La
 * variante `warning` cambia el fondo a crema (`bg-soft-warning`) y los
 * colores de texto a `text-on-warning*`.
 */
import type { ReactNode } from 'react';

type StatVariant = 'default' | 'warning';

interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  /** Parte del value que va en gris menor (ej: "de 12"). */
  valueFaded?: ReactNode;
  meta?: ReactNode;
  variant?: StatVariant;
  className?: string;
}

export function StatCard({
  label,
  value,
  valueFaded,
  meta,
  variant = 'default',
  className = '',
}: StatCardProps) {
  const isWarning = variant === 'warning';

  const containerClass = isWarning
    ? 'bg-soft-warning'
    : 'bg-bg-sunken';

  const labelClass = isWarning
    ? 'text-text-on-warning-soft'
    : 'text-text-secondary';

  const valueClass = isWarning ? 'text-text-on-warning' : 'text-text-primary';

  const metaClass = isWarning
    ? 'text-text-on-warning-soft'
    : 'text-text-secondary';

  return (
    <div
      className={`${containerClass} rounded-md p-5 ${className}`.trim()}
    >
      <div className={`text-sm mb-2 ${labelClass}`}>{label}</div>
      <div className={`text-3xl font-medium leading-tight ${valueClass}`}>
        {value}
        {valueFaded && (
          <span className="text-md text-text-muted font-regular ml-1">
            {valueFaded}
          </span>
        )}
      </div>
      {meta && <div className={`text-sm mt-1 ${metaClass}`}>{meta}</div>}
    </div>
  );
}
